import { createFileRoute } from '@tanstack/react-router';
import type { ReactElement } from 'react';
import { z } from 'zod';

import type { GateStatus } from '~/app/gate';
import { Require } from '~/app/require';
import { CodePage } from '~/features/invite/code-page';
import { InviteFlow } from '~/features/invite/invite-flow';
import { useDeadToken } from '~/features/invite/invite-preview';
import { captureInviteFromUrl, usePendingInvite } from '~/features/invite/pending-invite';
import { useLandingToken } from '~/features/invite/use-join-landing';

/** The router reads each query value as JSON, so an all-digit code arrives as a number. */
const text = z.union([z.string(), z.number()]).transform(String);

const search = z.object({ code: text.optional(), proof: text.optional() });

/** The typed code: for a signed-in person, before a first workspace or beside one. */
const TYPED: readonly GateStatus[] = ['needs-language', 'needs-onboarding', 'signed-in'];
/**
 * An invitation opens in every state but one (invite-onboarding, step 2.2). A
 * person with no name is sent to Get started to give it first, and comes back.
 */
const EVERY: readonly GateStatus[] = ['signed-out', 'needs-language', 'needs-onboarding', 'signed-in'];

function JoinRoute(): ReactElement {
  const { code } = Route.useSearch();
  const pending = usePendingInvite();
  const dead = useDeadToken();
  // A claim that joined has already cleared the invitation; its landing keeps the page.
  const landing = useLandingToken();
  const token = code ?? pending?.token ?? landing ?? dead;
  if (token === null) {
    return (
      <Require allow={TYPED} invitePage>
        <CodePage code={undefined} />
      </Require>
    );
  }
  return (
    <Require allow={EVERY} invitePage>
      <InviteFlow token={token} />
    </Require>
  );
}

/**
 * Joining a workspace: the invitation a link opened (or the one waiting in
 * this browser), or a code typed by hand (canvas 10-join).
 */
export const Route = createFileRoute('/join')({
  validateSearch: search,
  // The emailed link is kept before any gate decides anything (invite-onboarding, Decided 8).
  beforeLoad: ({ location }) => {
    if (typeof window !== 'undefined') captureInviteFromUrl(new URL(location.href, window.location.origin).href);
  },
  component: JoinRoute,
});
